import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import type { ScoutAroundUser } from '@/types/api';

export const userKeys = {
  all: ['users'] as const,
  search: (email: string) => [...userKeys.all, 'search', email] as const,
  validate: (email: string) => [...userKeys.all, 'validate', email] as const,
};

interface UserSearchResponse {
  users: ScoutAroundUser[];
}

interface UserValidateResponse {
  valid: boolean;
  user: {
    userId: number;
    email: string;
    name: string;
  } | null;
}

/**
 * Search for ScoutAround users by email prefix.
 * Returns users matching the email search term (minimum 3 characters).
 * Results are cached for 30 seconds.
 */
export function useUserSearch(emailPrefix: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.search(emailPrefix),
    queryFn: () =>
      api.get<UserSearchResponse>(`/api/v1/users/search?email=${encodeURIComponent(emailPrefix)}`),
    enabled: (options?.enabled ?? true) && emailPrefix.length >= 3,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Validate an email address and get user details.
 * Returns whether the email exists in ScoutAround and the user info if found.
 */
export function useValidateUserEmail(email: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.validate(email),
    queryFn: () =>
      api.get<UserValidateResponse>(`/api/v1/users/validate?email=${encodeURIComponent(email)}`),
    enabled: (options?.enabled ?? true) && email.length >= 3,
    staleTime: 30000, // 30 seconds
  });
}
